"use client";

import { clearChunkReloadFlag, isChunkLoadError, reloadForChunkError } from "@/shared/chunk-reload";
import { useEffect } from "react";

/**
 * Listens for uncaught errors and unhandled promise rejections caused by stale
 * chunks after a redeploy, and performs a one-time hard reload to recover.
 * This complements the React error boundaries by catching failures that happen
 * outside of render (e.g. dynamic import() rejections and async event handlers).
 */
export function ChunkErrorReloadHandler() {
  useEffect(() => {
    // Only clear the reload guard once the app has stayed stable for a short
    // while. Clearing it immediately on mount would defeat the guard: a
    // persistent error that fires right after mount would find the flag gone
    // and trigger another reload, causing an infinite reload loop.
    const clearFlagTimeout = window.setTimeout(clearChunkReloadFlag, 5000);

    function handleError(event: ErrorEvent) {
      if (isChunkLoadError(event.error ?? event.message)) {
        reloadForChunkError();
      }
    }

    function handleRejection(event: PromiseRejectionEvent) {
      if (isChunkLoadError(event.reason)) {
        reloadForChunkError();
      }
    }

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.clearTimeout(clearFlagTimeout);
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  return null;
}
